/**
 * Multi-Campaign Layout Utilities
 * 
 * Adjusts task positions to account for campaign zone boundaries.
 * When tasks from multiple campaigns are displayed together, this ensures
 * proper spacing so campaign zones don't overlap.
 */

import { DEFAULT_NODE_WIDTH, DEFAULT_NODE_HEIGHT, GAP } from './constants';

// Padding added to campaign zone boundaries (should match CampaignZones component)
const ZONE_PADDING = 50;

// Gap between adjacent campaign zones
const INTER_CAMPAIGN_GAP = 30;

/**
 * Adjust positions for multi-campaign layout
 * 
 * Ensures each campaign's tasks are grouped together with proper spacing
 * so that campaign zones (with their padding) don't overlap.
 * 
 * Each campaign's internal layout is preserved - this function only applies
 * horizontal offsets to separate campaign zones from each other.
 * 
 * @param {Array} tasks - Tasks with sourceCampaign metadata and position property
 * @param {Object} nodeDimensions - Map of taskId -> { width, height }
 * @returns {Object} Map of taskId -> { x, y } adjusted positions
 */
export function layoutMultiCampaignTasks(tasks, nodeDimensions = {}) {
  if (!tasks || tasks.length === 0) {
    return {};
  }

  // Group tasks by campaign
  const tasksByCampaign = {};
  const campaignNames = {}; // Track campaign names for sorting
  
  tasks.forEach(task => {
    const campaignId = task.sourceCampaign?.id || 'unknown';
    if (!tasksByCampaign[campaignId]) {
      tasksByCampaign[campaignId] = [];
      campaignNames[campaignId] = task.sourceCampaign?.name || 'Unknown';
    }
    tasksByCampaign[campaignId].push(task);
  });
  
  // Sort campaigns alphabetically by name for consistent ordering
  const campaignOrder = Object.keys(tasksByCampaign).sort((a, b) => {
    return campaignNames[a].localeCompare(campaignNames[b]);
  });

  // If only one campaign, just use the positions from tasks
  if (campaignOrder.length <= 1) {
    const positions = {};
    tasks.forEach(task => {
      positions[task.id] = task.position || { x: 0, y: 0 };
    });
    return positions;
  }

  // Calculate bounds for each campaign based on the positions in task.position
  // (which should already have the intra-campaign layout applied)
  const campaignBounds = {};
  campaignOrder.forEach(campaignId => {
    const campaignTasks = tasksByCampaign[campaignId];
    let minX = Infinity, maxX = -Infinity;
    let minY = Infinity, maxY = -Infinity;

    campaignTasks.forEach(task => {
      const pos = task.position || { x: 0, y: 0 };
      const dims = nodeDimensions[task.id] || { 
        width: DEFAULT_NODE_WIDTH, 
        height: DEFAULT_NODE_HEIGHT 
      };

      minX = Math.min(minX, pos.x);
      maxX = Math.max(maxX, pos.x + dims.width);
      minY = Math.min(minY, pos.y);
      maxY = Math.max(maxY, pos.y + dims.height);
    });

    campaignBounds[campaignId] = { minX, maxX, minY, maxY };
  });

  // Calculate the required horizontal offset for each campaign to prevent zone overlap
  // Zones are laid out left-to-right in campaign order
  const campaignOffsets = {};
  let currentRightEdge = 0;

  campaignOrder.forEach((campaignId, index) => {
    const bounds = campaignBounds[campaignId];
    const campaignWidth = bounds.maxX - bounds.minX;

    if (index === 0) {
      // First campaign: position so its leftmost node is at x=100
      const targetMinX = 100;
      campaignOffsets[campaignId] = targetMinX - bounds.minX;
      // Track where this campaign's zone right edge is
      currentRightEdge = targetMinX + campaignWidth + ZONE_PADDING;
    } else {
      // Subsequent campaigns: position after previous campaign's zone + gap
      const targetMinX = currentRightEdge + INTER_CAMPAIGN_GAP + ZONE_PADDING;
      campaignOffsets[campaignId] = targetMinX - bounds.minX;
      // Update for next campaign
      currentRightEdge = targetMinX + campaignWidth + ZONE_PADDING;
    }
  });

  // Apply horizontal offsets to all tasks (preserve Y positions)
  const positions = {};
  tasks.forEach(task => {
    const campaignId = task.sourceCampaign?.id || 'unknown';
    const xOffset = campaignOffsets[campaignId] || 0;
    const originalPos = task.position || { x: 0, y: 0 };

    positions[task.id] = {
      x: originalPos.x + xOffset,
      y: originalPos.y, // Keep original Y - intra-campaign layout handles vertical positioning
    };
  });

  return positions;
}

/**
 * Get the total width required for multi-campaign layout
 * Useful for setting canvas bounds or centering
 */
export function getMultiCampaignLayoutWidth(tasks, nodeDimensions = {}) {
  const positions = layoutMultiCampaignTasks(tasks, nodeDimensions);
  
  let maxX = 0;
  tasks.forEach(task => {
    const pos = positions[task.id] || { x: 0, y: 0 };
    const dims = nodeDimensions[task.id] || { 
      width: DEFAULT_NODE_WIDTH, 
      height: DEFAULT_NODE_HEIGHT 
    };
    maxX = Math.max(maxX, pos.x + dims.width);
  });

  return maxX + ZONE_PADDING; // Include final zone padding
}
